/**
 * Conquest flash — a transient border flash on a territory that just changed
 * ownership via a conquering attack.
 *
 * Each flash is a thick stroke of the territory outline in the new owner's
 * player colour, plus a wider, fainter halo. The core runs 1 → 0 over ~900ms
 * (100ms hold at full opacity, then 800ms fade); the halo fades a little
 * slower so it reads as a glow around a ring.
 *
 * Reduced motion: the animation is replaced by a solid stroke that holds for
 * 1500ms then snaps off (no transition).
 *
 * Drawn ONCE, faded natively: each stroke is a static path in its own overlay
 * <Svg> (MapOverlayLayer) and the fade animates that view's opacity. It used to
 * animate `strokeOpacity` on the path, and react-native-svg on iOS redraws the
 * whole <Svg> for every animated-prop frame — re-stroking the outline twice
 * per frame, plus the attack badges and fortify trail sharing that layer. On
 * Arctic, whose outlines carry ~6x Carpathia's path data, that is what made
 * the capture glow stutter (docs/map-perf.md). For a single path, view opacity
 * composites exactly like strokeOpacity.
 *
 * The flashes sit above the selection overlay and below the effects layer
 * (attack badges, fortify trail) and the troop numbers.
 */

import React from 'react'
import { Path } from 'react-native-svg'
import {
  useSharedValue,
  useAnimatedStyle,
  withSequence,
  withTiming,
  Easing,
  useReducedMotion,
} from 'react-native-reanimated'
import { conquestFlashKey, useConquestEvents, type ConquestAnimationEntry } from '../../hooks/useConquestEvents'
import type { GameEvent } from '@warcarp/api-types'
import { MapOverlayLayer, type MapOverlayFrame } from './MapOverlayLayer'

// ─── Individual flash ────────────────────────────────────────────────────────

interface FlashOverlaysProps {
  frame: MapOverlayFrame
  d: string
  color: string
  strokeWidth: number
  reducedMotion: boolean
}

function FlashOverlays({ frame, d, color, strokeWidth, reducedMotion }: FlashOverlaysProps) {
  // Two synchronised sharedValues drive the inner stroke + the wider
  // outer halo. The halo holds at lower base opacity and fades a
  // little slower so the user reads a "glow + ring" rather than a
  // single thick line.
  const opacity = useSharedValue(1)
  const haloOpacity = useSharedValue(0.55)

  React.useEffect(() => {
    if (reducedMotion) {
      // Reduced motion: hold at 1 / 0.55 for 1500ms, snap to 0.
      opacity.value = 1
      haloOpacity.value = 0.55
      const handle = setTimeout(() => {
        opacity.value = 0
        haloOpacity.value = 0
      }, 1500)
      return () => clearTimeout(handle)
    } else {
      // Full animation: 100ms hold at full opacity, 800ms fade.
      opacity.value = withSequence(
        withTiming(1, { duration: 100 }),
        withTiming(0, { duration: 800, easing: Easing.out(Easing.quad) }),
      )
      // Halo starts mid-opacity, peaks slightly later, then fades over
      // a longer window so the glow lingers after the core stroke is
      // gone — reads as a fading aura around the captured territory.
      haloOpacity.value = withSequence(
        withTiming(0.55, { duration: 80 }),
        withTiming(0.4, { duration: 200 }),
        withTiming(0, { duration: 900, easing: Easing.out(Easing.quad) }),
      )
    }
  // Only run on mount — the flash starts once and doesn't change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const coreStyle = useAnimatedStyle(() => ({ opacity: opacity.value }))
  const haloStyle = useAnimatedStyle(() => ({ opacity: haloOpacity.value }))

  return (
    <>
      {/* Outer halo — wider stroke, painted FIRST so the core renders on
          top. Same colour; the wider radius + softer opacity reads as a
          glow around the territory's outline rather than a second line. */}
      <MapOverlayLayer {...frame} style={haloStyle}>
        <Path
          d={d}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth * 2.4}
          strokeLinejoin="round"
          strokeLinecap="round"
          pointerEvents="none"
        />
      </MapOverlayLayer>
      {/* Inner stroke — original border flash. */}
      <MapOverlayLayer {...frame} style={coreStyle}>
        <Path
          d={d}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinejoin="round"
          strokeLinecap="round"
          pointerEvents="none"
        />
      </MapOverlayLayer>
    </>
  )
}

// ─── Static ring (no animation) ──────────────────────────────────────────────

interface StaticRingProps {
  d: string
  color: string
  strokeWidth: number
}

/**
 * Frozen conquest ring — same halo + core stroke as a live flash but
 * locked at full opacity with no fade. Used by the time-machine
 * snapshot view so the conquered territory wears its new owner's
 * colour ring as a persistent affordance while the user reviews the
 * snapshot.
 */
function StaticRing({ d, color, strokeWidth }: StaticRingProps) {
  return (
    <>
      <Path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth * 2.4}
        strokeLinejoin="round"
        strokeLinecap="round"
        strokeOpacity={0.45}
        pointerEvents="none"
      />
      <Path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
        pointerEvents="none"
      />
    </>
  )
}

// ─── Layers ──────────────────────────────────────────────────────────────────

interface ConquestFlashOverlaysProps {
  events: GameEvent[] | undefined
  slotByPlayerId: Record<string, number>
  playerColorHex: (slot: number) => string
  /** Territory shapes keyed by territory id. */
  shapeById: Record<string, string | undefined>
  /** Flash stroke width in SVG viewBox units. */
  strokeWidth: number
  frame: MapOverlayFrame
}

/**
 * Live conquest flashes, rendered beside the map's overlay <Svg>s (not inside
 * one). Owns the conquest-event hook so its state churn re-renders only this
 * memoized sibling.
 */
export const ConquestFlashOverlays = React.memo(function ConquestFlashOverlays({
  events,
  slotByPlayerId,
  playerColorHex,
  shapeById,
  strokeWidth,
  frame,
}: ConquestFlashOverlaysProps) {
  const entries = useConquestEvents(events, slotByPlayerId, playerColorHex)
  const reducedMotion = useReducedMotion()
  return (
    <>
      {entries.map((entry) => {
        const d = shapeById[entry.territoryId]
        if (!d) return null
        return (
          <FlashOverlays
            // Use source event identity: multiple conquests in one update
            // share startedAt but need independent animation components.
            key={conquestFlashKey(entry)}
            frame={frame}
            d={d}
            color={entry.ownerColor}
            strokeWidth={strokeWidth}
            reducedMotion={reducedMotion}
          />
        )
      })}
    </>
  )
})

interface ConquestRingLayerProps {
  /**
   * The time-machine snapshot's frozen conquest highlight on a captured
   * territory. Full opacity, no halo decay, no fade.
   */
  entry?: ConquestAnimationEntry
  shapeById: Record<string, string | undefined>
  strokeWidth: number
}

/** Static conquest ring, drawn inside the effects <Svg> (it never animates). */
export function ConquestRingLayer({ entry, shapeById, strokeWidth }: ConquestRingLayerProps) {
  const d = entry ? shapeById[entry.territoryId] : undefined
  if (!entry || !d) return null
  return <StaticRing d={d} color={entry.ownerColor} strokeWidth={strokeWidth} />
}
